import { AlertTriangle, CheckCircle, ShieldAlert } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../lib/cn";

export interface HealthSummaryCardProps {
	status: "healthy" | "degraded" | "tampered";
	headline: string;
	subtext: string;
	lastAnchorAt: string | null;
	eventsLast24h: number;
	className?: string;
}

const STATUS_CONFIG = {
	healthy: {
		icon: CheckCircle,
		iconClass: "text-statusSuccess",
		borderClass: "border-statusSuccess/30",
		bgClass: "bg-statusSuccess/5",
	},
	degraded: {
		icon: AlertTriangle,
		iconClass: "text-statusWarning",
		borderClass: "border-statusWarning/30",
		bgClass: "bg-statusWarning/5",
	},
	tampered: {
		icon: ShieldAlert,
		iconClass: "text-destructive",
		borderClass: "border-destructive/30",
		bgClass: "bg-destructive/5",
	},
} as const;

/**
 * Layperson-friendly system health card. Uses plain-language status labels
 * and avoids technical jargon. Three visual states map to green/amber/red.
 */
function HealthSummaryCard({
	status,
	headline,
	subtext,
	lastAnchorAt,
	eventsLast24h,
	className,
}: HealthSummaryCardProps): ReactNode {
	const config = STATUS_CONFIG[status];
	const Icon = config.icon;

	const formattedLastAnchor = lastAnchorAt
		? new Date(lastAnchorAt).toLocaleString()
		: "No anchors yet";

	return (
		<div
			className={cn(
				"flex flex-col gap-3 rounded-lg border p-5",
				config.borderClass,
				config.bgClass,
				className,
			)}
			aria-label={`System status: ${status}`}
		>
			<div className="flex items-start gap-3">
				<Icon
					className={cn("mt-0.5 h-5 w-5 shrink-0", config.iconClass)}
					aria-hidden="true"
				/>
				<div className="flex flex-col gap-0.5">
					<span className="text-sm font-semibold text-foreground">
						{headline}
					</span>
					<span className="text-xs text-mutedForeground">{subtext}</span>
				</div>
			</div>
			<div className="flex flex-col gap-1 text-xs text-mutedForeground">
				<span>
					<span className="font-medium text-foreground">{eventsLast24h}</span>{" "}
					events in the last 24 hours
				</span>
				<span>Last anchor: {formattedLastAnchor}</span>
			</div>
		</div>
	);
}
HealthSummaryCard.displayName = "HealthSummaryCard";

export default HealthSummaryCard;
export { HealthSummaryCard };
